import type { ModuleStudioRepository } from '../data/ModuleStudioRepository';
import type { DesignModuleSpecJson } from '../data/contracts';
import { currentModel } from '../simulation/liveSimulationStep';
import { healRecaptureDecisions, restartFromCapture } from '../template/restartFromCapture';
import { restoreSections, type LostSection } from '../template/lostSections';
import { CAPTURE_SCREENS, RESTART_FROM_CAPTURE, RESTORE_SECTIONS } from './planPreflight';
import { PROPOSE_COMPUTED } from './computedFieldsStep';

/** E2E-FIX1 (I, J, K) : les gestes du Maker que le plan insère, exécutés dans le navigateur. Un geste qui change le modèle passe par
 * l'enregistrement du brouillon du Designer (repository.putModuleSpecDraft : décisions journalisées, origines compactées). */
export const CLIENT_STEPS = new Set([CAPTURE_SCREENS, RESTART_FROM_CAPTURE, RESTORE_SECTIONS, PROPOSE_COMPUTED]);
export const sectionKey = (section: LostSection) => section.page + '|' + section.version + '|' + section.path.join('.');

/** I : l'étape « Capturer les écrans » se termine quand l'import vivant porte au moins une capture (parcours fait par le Maker). */
export async function capturesPresent(repository: ModuleStudioRepository, dossier: string): Promise<boolean> {
  const live = (await repository.listPrototypeImports(dossier)).find(item => item.status === 'imported' || item.status === 'analysed');
  if (!live) return false;
  return (await repository.getPrototypeImport(live.id)).pieces.some(piece => piece.kind === 'snapshot');
}

type Run = (key: 'save-draft', parameters: Record<string, unknown>) => Promise<unknown>;
/** Le brouillon courant (créé depuis la version de travail s'il n'existe pas encore), changé par le geste puis enregistré. */
export async function saveGestureDraft(repository: ModuleStudioRepository, dossier: string, change: (spec: DesignModuleSpecJson) => DesignModuleSpecJson | Promise<DesignModuleSpecJson>, run: Run): Promise<void> {
  let draft = await repository.getModuleSpecDraft(dossier);
  if (!draft) {
    const { source } = await currentModel(repository, dossier);
    if (source.kind !== 'version' || source.version === null) throw new Error('studio.command.draft_required');
    await run('save-draft', { fromVersion: source.version });
    draft = await repository.getModuleSpecDraft(dossier);
    if (!draft) throw new Error('studio.command.draft_required');
  }
  // GAP-31 : un brouillon qui porte des décisions « Repartir de la capture » à l'ancien format est guéri au passage.
  await repository.putModuleSpecDraft(dossier, { spec: await healRecaptureDecisions(await change(draft.spec)), baseChecksum: draft.checksum, ...(draft.layout ? { layout: draft.layout } : {}) });
}

export async function restartPages(repository: ModuleStudioRepository, dossier: string, pages: readonly string[], run: Run): Promise<void> {
  if (!pages.length) return;
  await saveGestureDraft(repository, dossier, spec => restartFromCapture(spec, pages), run);
}

export async function restoreChosenSections(repository: ModuleStudioRepository, dossier: string, sections: readonly LostSection[], run: Run): Promise<void> {
  if (!sections.length) return;
  const older = new Map<number, DesignModuleSpecJson>();
  for (const version of [...new Set(sections.map(section => section.version))]) older.set(version, (await repository.getModuleSpecVersion(dossier, version)).spec);
  await saveGestureDraft(repository, dossier, spec => {
    let next = spec;
    for (const [version, source] of older) next = restoreSections(next, source, sections.filter(section => section.version === version));
    return next;
  }, run);
}
